import { OntologyPromotionError } from "./errors";
import {
  ontologyReleaseManifestHash,
  sha256Text,
  stableStringify,
  statementSetHash,
} from "./stable-hash";
import type {
  OntologyAdmissionReceiptVerifier,
  OntologyEvidenceRef,
  OntologyReleaseReadback,
  OntologyReleaseSelection,
} from "./types";

const HASH_PATTERN = /^sha256:[0-9a-f]{64}$/;
const RELEASE_PATTERN = /^release-[0-9a-f]{24}$/;
const SCHEMA_VERSION = "cogo.ontology-release-selection.v1" as const;

export async function buildOntologyReleaseSelection(
  readback: OntologyReleaseReadback,
  selectedAt = new Date().toISOString(),
  admissionVerifier?: OntologyAdmissionReceiptVerifier,
): Promise<OntologyReleaseSelection> {
  const { pointer, release } = readback;
  if (!pointer || !release) {
    throw exactReadbackError(
      "Active pointer and committed release are required.",
      readback,
    );
  }

  const observedStatementHash = await statementSetHash(readback.statements);
  const observedEvidence = normalizeEvidenceRefs(readback.evidenceRefs);
  const manifestEvidence = normalizeEvidenceRefs(release.evidenceRefs);
  const observedEvidenceHash = await sha256Text(
    stableStringify(observedEvidence),
  );
  const observedManifestHash = await ontologyReleaseManifestHash(release);
  const admissionAuthorityVerified =
    release.schemaVersion === "cogo.ontology-release-manifest.v1"
      ? true
      : admissionVerifier
        ? (await admissionVerifier.verify(release.admissionReceipt)) &&
          release.admissionReceipt.targetScope === "project" &&
          release.admissionReceipt.decision === "approved" &&
          release.admissionReceipt.tenantId === release.tenantId &&
          release.admissionReceipt.appId === release.appId &&
          release.admissionReceipt.branch === release.branch &&
          release.admissionReceipt.sourceSetHash === release.sourceSetHash &&
          release.admissionReceipt.statementHash === release.statementHash &&
          release.admissionReceipt.evidenceSetHash === observedEvidenceHash
        : false;
  const exact =
    pointer.releaseId === release.releaseId &&
    pointer.tenantId === release.tenantId &&
    pointer.appId === release.appId &&
    pointer.branch === release.branch &&
    release.status === "committed" &&
    release.manifestHash === observedManifestHash &&
    release.releaseId === releaseIdFromManifestHash(release.manifestHash) &&
    admissionAuthorityVerified &&
    release.statementCount === readback.statements.length &&
    release.statementHash === observedStatementHash &&
    stableStringify(observedEvidence) === stableStringify(manifestEvidence);

  if (!exact) {
    throw exactReadbackError(
      `Release ${release.releaseId} cannot be selected because its readback is not exact.`,
      {
        pointer,
        release,
        observedStatementHash,
        observedEvidenceHash,
        observedManifestHash,
        admissionAuthorityVerified,
        observedStatementCount: readback.statements.length,
        observedEvidence,
      },
    );
  }

  const selection = parseOntologyReleaseSelection({
    schemaVersion: SCHEMA_VERSION,
    tenantId: release.tenantId,
    appId: release.appId,
    branch: release.branch,
    releaseId: release.releaseId,
    pointerVersion: pointer.pointerVersion,
    manifestHash: release.manifestHash,
    statementHash: release.statementHash,
    statementCount: release.statementCount,
    tboxVersion: release.tboxVersion,
    ruleVersion: release.ruleVersion,
    sourceSetHash: release.sourceSetHash,
    evidenceRefs: manifestEvidence,
    selectedAt,
    exactReadback: true,
  });
  if (!selection) {
    throw exactReadbackError(
      "Exact readback produced an invalid consumer selection.",
      readback,
    );
  }
  return selection;
}

export function parseOntologyReleaseSelection(
  value: unknown,
): OntologyReleaseSelection | null {
  const record = readRecord(value);
  if (
    !record ||
    record.schemaVersion !== SCHEMA_VERSION ||
    record.exactReadback !== true
  ) {
    return null;
  }

  const tenantId = readNonEmptyString(record.tenantId);
  const appId = readNonEmptyString(record.appId);
  const branch = readNonEmptyString(record.branch);
  const releaseId = readNonEmptyString(record.releaseId);
  const manifestHash = readHash(record.manifestHash);
  const statementHash = readHash(record.statementHash);
  const sourceSetHash = readHash(record.sourceSetHash);
  const tboxVersion = readNonEmptyString(record.tboxVersion);
  const ruleVersion = readNonEmptyString(record.ruleVersion);
  const selectedAt = readIsoDate(record.selectedAt);
  const pointerVersion = readPositiveInteger(record.pointerVersion);
  const statementCount = readPositiveInteger(record.statementCount);
  const evidenceRefs = parseEvidenceRefs(record.evidenceRefs);

  if (
    !tenantId ||
    !appId ||
    !branch ||
    !releaseId ||
    !manifestHash ||
    !statementHash ||
    !sourceSetHash ||
    !tboxVersion ||
    !ruleVersion ||
    !selectedAt ||
    pointerVersion === null ||
    statementCount === null ||
    !evidenceRefs ||
    !RELEASE_PATTERN.test(releaseId) ||
    releaseId !== releaseIdFromManifestHash(manifestHash)
  ) {
    return null;
  }

  return {
    schemaVersion: SCHEMA_VERSION,
    tenantId,
    appId,
    branch,
    releaseId,
    pointerVersion,
    manifestHash,
    statementHash,
    statementCount,
    tboxVersion,
    ruleVersion,
    sourceSetHash,
    evidenceRefs,
    selectedAt,
    exactReadback: true,
  };
}

function parseEvidenceRefs(value: unknown): OntologyEvidenceRef[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const parsed: OntologyEvidenceRef[] = [];
  for (const item of value) {
    const record = readRecord(item);
    const runId = readNonEmptyString(record?.runId);
    const artifactHash = readHash(record?.artifactHash);
    const mediaType = readNonEmptyString(record?.mediaType);
    const uri = readNonEmptyString(record?.uri);
    if (!runId || !artifactHash || !mediaType || !uri) return null;
    parsed.push({ runId, artifactHash, mediaType, uri });
  }
  const normalized = normalizeEvidenceRefs(parsed);
  return new Set(normalized.map(stableStringify)).size === normalized.length
    ? normalized
    : null;
}

function normalizeEvidenceRefs(
  evidenceRefs: OntologyEvidenceRef[],
): OntologyEvidenceRef[] {
  return evidenceRefs
    .map((item) => ({ ...item }))
    .sort((left, right) =>
      stableStringify(left).localeCompare(stableStringify(right)),
    );
}

function releaseIdFromManifestHash(manifestHash: string): string {
  return `release-${manifestHash.slice("sha256:".length, "sha256:".length + 24)}`;
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readNonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function readHash(value: unknown): string | null {
  return typeof value === "string" && HASH_PATTERN.test(value) ? value : null;
}

function readPositiveInteger(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value > 0
    ? value
    : null;
}

function readIsoDate(value: unknown): string | null {
  return typeof value === "string" && Number.isFinite(Date.parse(value))
    ? value
    : null;
}

function exactReadbackError(
  message: string,
  details: unknown,
): OntologyPromotionError {
  return new OntologyPromotionError("EXACT_READBACK_FAILED", message, details);
}
